import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Ban, ClipboardList, Download, Eye, Info, LockKeyhole, PackageCheck } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { DateRangePicker } from '../../../../components/ui/DateRangePicker';
import { CountChips } from '../../../../components/ui/CountChips';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { useNotice } from '../../../../hooks/useNotice';
import { OrderStateCell } from './components/OrderStateCell';
import { PreparedCell } from './components/PreparedCell';
import {
  cancelWmsOrder,
  cancelWmsOrdersBulk,
  fetchWmsOrders,
  reserveWmsOrder,
  reserveWmsOrdersBulk,
} from '../../../../features/wmsOrders/wmsOrdersThunks';
import { setWmsOrderFilters } from '../../../../features/wmsOrders/wmsOrdersSlice';
import {
  ALL_FILTER,
  CLOSED_STATES,
  FLOW_STATES,
  SOURCE_OPTIONS,
  buildOrdersExportRows,
  cancelConsequences,
  cancelDoneNotice,
  formatOrderDate,
  bulkDoneNotice,
  isCancellableState,
  isOpenState,
  isReservableState,
  rowCanCancel,
  rowCanReserve,
  ordersExportFileName,
  orderTitle,
} from '../../../../features/wmsOrders/wmsOrdersUtils';
import {
  selectCurrentWarehouse,
  selectHeldWarehouseIds,
  selectReservableWarehouseIds,
  selectWarehouse,
  selectWarehouseClientOptions,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import { reserveDoneNotice } from '../../../../features/fulfillment/fulfillmentUtils';
import { selectCanWms } from '../../../../features/auth/authSelectors';
import {
  orderSourceLabel,
  wmsOrderStateColor,
  wmsOrderStateLabel,
} from '../../../../utils/inventoryLabels';

const ORDERS_PATH = '/warehouse/orders';

// Cancelling belongs to the owner of the order: the seller cancels its own.
const selectCanCancel = selectCanWms('order:cancel');

/**
 * `/warehouse/orders`: the seller's orders as seen from inside the warehouse.
 *
 * The state of each one is derived by the server from its reservations, what
 * was prepared and its parcels; the chips above are the day's work, in the
 * order an order walks through the warehouse. Two notes keep an empty table
 * from looking like a failure: since when the listing looks, and how many
 * orders sit in other warehouses than the one in use.
 *
 * The seller follows and cancels everywhere; it prepares only where the
 * warehouse is its own. Holding the space is what opens that door, not the
 * role, so it is decided order by order. In its operator's centre it may
 * still reserve an entered order, when the contract lets it: just that.
 */
export const WarehouseOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    orders,
    totalCount,
    stateCounts,
    windowFrom,
    elsewhere,
    loading,
    error,
    filters,
    cancellingId,
    reservingId,
    bulkBusy,
  } = useSelector((state) => state.wmsOrders);
  const warehouse = useSelector(selectCurrentWarehouse);
  const clientOptions = useSelector(selectWarehouseClientOptions);
  const canCancel = useSelector(selectCanCancel);
  const heldWarehouseIds = useSelector(selectHeldWarehouseIds);
  const reservableWarehouseIds = useSelector(selectReservableWarehouseIds);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [toCancel, setToCancel] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkCancelling, setBulkCancelling] = useState(false);
  const setNotice = useNotice('orders-notice');

  // The seller reserves where it holds the warehouse (through "Preparar") and
  // where its contract opens the reserve; the bulk follows the same map.
  const canReserveRow = (row) =>
    rowCanReserve(
      row,
      heldWarehouseIds.includes(row.warehouse?.id) || reservableWarehouseIds.includes(row.warehouse?.id)
    );
  const canCancelRow = (row) => rowCanCancel(row, canCancel);
  const mayReserveSome = heldWarehouseIds.length > 0 || reservableWarehouseIds.length > 0;

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setWmsOrderFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when the warehouse in use or a filter changes
  useEffect(() => {
    dispatch(fetchWmsOrders());
  }, [
    dispatch,
    warehouse?.id,
    filters.status,
    filters.company,
    filters.source,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.page,
    filters.rowsPerPage,
  ]);


  const showClient = clientOptions.length > 1;

  const handleCancel = async () => {
    const action = await dispatch(cancelWmsOrder(toCancel.id));
    setToCancel(null);
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? cancelDoneNotice(action.payload)
        : { type: 'error', message: action.payload }
    );
  };

  // Reserving holds stock without moving it, so there is nothing to confirm:
  // the notice says how much was taken and how many lines are covered.
  const handleReserve = async (row) => {
    const action = await dispatch(reserveWmsOrder(row.id));
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? reserveDoneNotice(action.payload)
        : { type: 'error', message: action.payload }
    );
  };

  // Bulk: the ticked rows that each action would accept, by the row rules.
  const selectedRows = orders.filter((row) => selected.includes(row.id));
  const reservableSelected = selectedRows.filter(canReserveRow);
  const cancellableSelected = selectedRows.filter(canCancelRow);
  const bulkCancelMessage = [
    `Las ${cancellableSelected.length} órdenes marcadas pasarán a Canceladas. No se eliminan: conservan su historial, sus reservas vuelven a disponible y lo ya pickeado queda en la mesa.`,
    cancellableSelected.some((row) => row.has_label)
      ? 'Alguna ya tiene etiqueta del courier: se intentará cancelar allá también.'
      : null,
  ]
    .filter(Boolean)
    .join(' ');

  // A page that changed under the selection keeps only what is still on it.
  useEffect(() => {
    setSelected((prev) => prev.filter((id) => orders.some((row) => row.id === id)));
  }, [orders]);

  const handleBulkReserve = async () => {
    const action = await dispatch(reserveWmsOrdersBulk(reservableSelected.map((row) => row.id)));
    if (action.meta.requestStatus === 'fulfilled') {
      setSelected([]);
      setNotice(bulkDoneNotice(action.payload));
    } else {
      setNotice({ type: 'error', message: action.payload });
    }
  };

  const handleBulkCancel = async () => {
    const action = await dispatch(cancelWmsOrdersBulk(cancellableSelected.map((row) => row.id)));
    setBulkCancelling(false);
    if (action.meta.requestStatus === 'fulfilled') {
      setSelected([]);
      setNotice(bulkDoneNotice(action.payload));
    } else {
      setNotice({ type: 'error', message: action.payload });
    }
  };

  const handleExport = () => {
    const sheet = XLSX.utils.json_to_sheet(buildOrdersExportRows(orders));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Órdenes');
    XLSX.writeFile(book, ordersExportFileName(warehouse));
  };

  const chipOf = (state) => ({
    value: state,
    label: wmsOrderStateLabel(state),
    count: stateCounts?.[state] ?? 0,
    tone: wmsOrderStateColor(state),
  });
  const chipsTotal = stateCounts
    ? Object.values(stateCounts).reduce((total, count) => total + count, 0)
    : null;

  const columns = [
    {
      header: 'ORDEN',
      cell: (row) => (
        <div className="flex flex-col">
          {/* The number opens the detail, like the eye at the end of the row:
              it is what the finger goes to first. */}
          <Link
            to={`${ORDERS_PATH}/${row.id}`}
            className="font-bold font-mono text-ninja-dark dark:text-white hover:text-ninja-green hover:underline"
            data-testid="order-link"
          >
            #{row.id}
          </Link>
          <span className="text-xs text-text-muted mt-0.5">{row.reference_id || 'Sin referencia'}</span>
        </div>
      ),
    },
    ...(showClient
      ? [
          {
            header: 'CLIENTE',
            cell: (row) => <span className="text-text-secondary">{row.company?.name || '-'}</span>,
          },
        ]
      : []),
    {
      // Who it goes to and where it came from: what somebody at the table
      // reads off the packing slip to find the order.
      header: 'DESTINATARIO',
      cell: (row) => (
        <div className="flex flex-col max-w-[240px]">
          <span className="text-ninja-dark dark:text-white truncate" title={row.recipient}>
            {row.recipient || '-'}
          </span>
          <span className="text-xs text-text-muted mt-0.5">
            {[orderSourceLabel(row.source), !warehouse && row.warehouse?.name]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      ),
    },
    { header: 'ESTADO', cell: (row) => <OrderStateCell order={row} /> },
    { header: 'PREPARADO', cell: (row) => <PreparedCell order={row} /> },
    {
      header: 'CREADA',
      cell: (row) => <span className="text-text-secondary">{formatOrderDate(row.created_at)}</span>,
    },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          {heldWarehouseIds.includes(row.warehouse?.id) &&
            row.module_active !== false &&
            isOpenState(row.wms_state) && (
              <Button
                variant="subtle"
                size="sm"
                iconOnly
                icon={PackageCheck}
                title="Preparar en tu bodega: pickear, armar bultos, etiquetar y despachar"
                aria-label={`Preparar la orden ${row.id}`}
                data-testid="order-fulfill"
                className="hover:text-ninja-green"
                onClick={() => navigate(`${ORDERS_PATH}/${row.id}/fulfill`)}
              />
            )}
          {reservableWarehouseIds.includes(row.warehouse?.id) &&
            !heldWarehouseIds.includes(row.warehouse?.id) &&
            row.module_active !== false &&
            isReservableState(row.wms_state) && (
              <Button
                variant="subtle"
                size="sm"
                iconOnly
                icon={LockKeyhole}
                loading={reservingId === row.id}
                title="Reservar: comprometer el stock de esta orden sin moverlo"
                aria-label={`Reservar la orden ${row.id}`}
                data-testid="order-reserve"
                className="hover:text-ninja-green"
                disabled={Boolean(reservingId)}
                onClick={() => handleReserve(row)}
              />
            )}
          {canCancel && row.module_active !== false && isCancellableState(row.wms_state) && (
            <Button
              variant="subtle"
              size="sm"
              iconOnly
              icon={Ban}
              loading={cancellingId === row.id}
              title="Cancelar antes de que salga: nada se borra"
              aria-label={`Cancelar la orden ${row.id}`}
              data-testid="order-cancel"
              className="hover:text-danger"
              disabled={Boolean(cancellingId)}
              onClick={() => setToCancel(row)}
            />
          )}
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Eye}
            title="Ver de qué se dedujo su estado"
            aria-label={`Ver la orden ${row.id}`}
            data-testid="order-view"
            onClick={() => navigate(`${ORDERS_PATH}/${row.id}`)}
          />
        </div>
      ),
    },
  ];

  // An empty table has to say why: empty looks the same as broken.
  const isFiltering =
    filters.search !== '' ||
    filters.company !== ALL_FILTER ||
    filters.source !== ALL_FILTER ||
    filters.dateFrom !== '';
  const emptyMessage = (() => {
    if (filters.status !== ALL_FILTER) {
      return `No hay órdenes en estado "${wmsOrderStateLabel(filters.status)}".`;
    }
    if (isFiltering) return 'No hay órdenes que coincidan con estos filtros.';
    return `No hay órdenes en ${warehouse ? warehouse.name : 'tus bodegas'}${
      windowFrom ? ` desde el ${formatOrderDate(windowFrom)}` : ''
    }.`;
  })();

  return (
    <div data-testid="warehouse-orders-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={ClipboardList}
        title="Órdenes"
        description="Los pedidos vistos desde la bodega. El estado no es un dato guardado: el servidor lo deduce de las reservas, de lo preparado y de los bultos."
      />

      <div className="space-y-3">
        <CountChips
          options={FLOW_STATES.map(chipOf)}
          value={filters.status}
          onChange={(status) => dispatch(setWmsOrderFilters({ status }))}
          allValue={ALL_FILTER}
          total={chipsTotal}
          testId="orders-flow"
        />
        {/* The ends apart from the stages: they are exceptions, not steps. */}
        <CountChips
          options={CLOSED_STATES.map(chipOf)}
          value={filters.status}
          onChange={(status) => dispatch(setWmsOrderFilters({ status }))}
          allValue={ALL_FILTER}
          showAll={false}
          testId="orders-closed"
        />
      </div>

      <FilterBar
        variant="bare"
        testId="orders-filters"
        actions={
          <Button
            variant="ghost"
            icon={Download}
            onClick={handleExport}
            disabled={orders.length === 0}
            title={orders.length > 0 ? 'Excel de las órdenes que estás viendo' : 'No hay órdenes que exportar'}
            data-testid="orders-export"
          >
            Exportar
          </Button>
        }
      >
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por orden, referencia o destinatario..."
          testId="orders-search"
        />
        <DateRangePicker
          startDate={filters.dateFrom}
          endDate={filters.dateTo}
          onChange={({ start_date, end_date }) =>
            dispatch(setWmsOrderFilters({ dateFrom: start_date, dateTo: end_date }))
          }
        />
        {showClient && (
          <FilterableSelect
            label="Cliente:"
            value={filters.company}
            onChange={(company) => dispatch(setWmsOrderFilters({ company }))}
            options={[{ value: ALL_FILTER, label: 'Todos' }, ...clientOptions]}
            searchPlaceholder="Buscar cliente..."
            panelMinWidth={240}
            testId="orders-client"
          />
        )}
        <FilterableSelect
          label="Canal:"
          value={filters.source}
          onChange={(source) => dispatch(setWmsOrderFilters({ source }))}
          options={SOURCE_OPTIONS}
          searchPlaceholder="Buscar canal..."
          testId="orders-source"
        />
      </FilterBar>

      {/* Since when the listing looks: without this, "I cannot see the order
          from March" reads as a failure when what is missing is the window. */}
      {windowFrom && !filters.dateFrom && (
        <p data-testid="orders-window" className="text-sm text-text-secondary">
          Mostrando desde el {formatOrderDate(windowFrom)}. Para ver más atrás, usa el filtro de
          fechas.
        </p>
      )}

      {/* The warehouse above is "where I work", and an order's is where it is
          prepared; for an operator they are rarely the same. */}
      {elsewhere && (
        <div
          data-testid="orders-elsewhere"
          className="flex flex-col sm:flex-row sm:items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900 rounded-xl px-4 py-3 text-sm text-ninja-dark dark:text-white"
        >
          <Info size={16} className="text-info flex-shrink-0" />
          <span className="flex-1">
            {orders.length === 0 && warehouse ? `En ${warehouse.name} no hay órdenes con estos filtros. ` : ''}
            Tienes <strong>{elsewhere.total}</strong> {elsewhere.total === 1 ? 'orden' : 'órdenes'} en
            otras bodegas.
            {elsewhere.warehouses.some((item) => item.selectable === false) &&
              ' Alguna no aparece en el selector porque no eres su titular: la administra tu cliente.'}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {elsewhere.warehouses.map((item) => (
              <ToneBadge key={item.id} tone="info">
                {item.name} · {item.count}
              </ToneBadge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch(selectWarehouse(null))}
              data-testid="orders-see-all-warehouses"
            >
              Ver todas mis bodegas
            </Button>
          </div>
        </div>
      )}

      {/* Twenty orders to reserve or cancel are one decision, not twenty clicks:
          tick the rows and the bar offers only what their own buttons would. */}
      {selected.length > 0 && (
        <div
          data-testid="orders-bulk-bar"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-ninja-green/40 bg-ninja-greenLight/40 dark:bg-ninja-green/10 px-4 py-3 text-sm"
        >
          <span className="font-semibold text-ninja-dark dark:text-white" data-testid="orders-bulk-count">
            {selected.length} {selected.length === 1 ? 'orden' : 'órdenes'}
          </span>
          {mayReserveSome && (
            <Button
              variant="primary"
              size="sm"
              icon={LockKeyhole}
              loading={bulkBusy === 'reserve'}
              loadingLabel="Reservando..."
              disabled={reservableSelected.length === 0 || Boolean(bulkBusy)}
              title={
                reservableSelected.length === 0
                  ? 'Ninguna de las marcadas está Ingresada'
                  : reservableSelected.length < selected.length
                    ? `${reservableSelected.length} de ${selected.length} están Ingresadas; el resto queda fuera`
                    : 'Compromete el stock de cada orden sin moverlo'
              }
              onClick={handleBulkReserve}
              data-testid="orders-bulk-reserve"
            >
              {reservableSelected.length === 0 ? 'Nada que reservar' : `Reservar ${reservableSelected.length}`}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="danger"
              size="sm"
              icon={Ban}
              disabled={cancellableSelected.length === 0 || Boolean(bulkBusy)}
              title={
                cancellableSelected.length === 0
                  ? 'Ninguna de las marcadas se puede cancelar'
                  : cancellableSelected.length < selected.length
                    ? `${cancellableSelected.length} de ${selected.length} se pueden cancelar; el resto queda fuera`
                    : 'Cancelar antes de que salgan: nada se borra'
              }
              onClick={() => setBulkCancelling(true)}
              data-testid="orders-bulk-cancel"
            >
              {cancellableSelected.length === 0 ? 'Nada que cancelar' : `Cancelar ${cancellableSelected.length}`}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSelected([])} data-testid="orders-bulk-clear">
            Quitar selección
          </Button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={orders}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchWmsOrders())}
        itemLabel="órdenes"
        emptyMessage={emptyMessage}
        onPageChange={(page) => dispatch(setWmsOrderFilters({ page }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setWmsOrderFilters({ rowsPerPage }))}
        selectedRows={selected}
        onSelectionChange={setSelected}
        isRowSelectable={(row) => canReserveRow(row) || canCancelRow(row)}
        selectionHint={() => 'Nada que hacer con esta orden desde acá'}
      />

      {/* Nothing is deleted. The dialog says what is reverted and what stays. */}
      <ConfirmDialog
        isOpen={Boolean(toCancel)}
        onClose={() => setToCancel(null)}
        onConfirm={handleCancel}
        title={toCancel ? `Cancelar la orden ${orderTitle(toCancel)}` : ''}
        message={toCancel ? cancelConsequences(toCancel).join(' ') : ''}
        confirmLabel="Cancelar orden"
        loadingLabel="Cancelando..."
        loading={Boolean(toCancel) && cancellingId === toCancel.id}
        testId="order-cancel-dialog"
      />

      <ConfirmDialog
        isOpen={bulkCancelling}
        onClose={() => setBulkCancelling(false)}
        onConfirm={handleBulkCancel}
        title={`Cancelar ${cancellableSelected.length} ${cancellableSelected.length === 1 ? 'orden' : 'órdenes'}`}
        message={bulkCancelMessage}
        confirmLabel="Cancelar órdenes"
        loadingLabel="Cancelando..."
        loading={bulkBusy === 'cancel'}
        testId="orders-bulk-cancel-dialog"
      />

    </div>
  );
};
